/**
 * Admin Agreement List Page
 * Shows all agreements (including archived ones) and entry points to create
 * a new agreement, view detail / versions, or archive an existing one.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { FormButton } from '../../components/form/FormButton';
import {
  useAgreementsList,
  useUpdateAgreementMeta,
} from '../../hooks/useAgreementApi';
import { AgreementFormModal } from '../../components/admin/AgreementFormModal';
import type { Agreement } from '../../lib/types/agreement';

export function AgreementListPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading, error } = useAgreementsList(true);
  const updateMeta = useUpdateAgreementMeta();

  const agreements: Agreement[] = useMemo(
    () => data?.agreements ?? [],
    [data?.agreements]
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Agreements
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Record user agreements such as Terms of Service or filming
              consent. Third-party apps use these to gate access.
            </p>
          </div>
          <FormButton type="button" onClick={() => setShowCreate(true)}>
            New agreement
          </FormButton>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <LoadingSpinner className="w-4 h-4" />
            <span>Loading…</span>
          </div>
        )}
        {error && (
          <p className="text-sm text-rose-600 dark:text-rose-400">
            Failed to load agreements.
          </p>
        )}
        {!isLoading && agreements.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No agreements yet. Create the first one.
          </p>
        )}

        {agreements.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name / slug
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Current version
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {agreements.map(a => (
                  <tr key={a.slug}>
                    <td className="px-4 py-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {a.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {a.slug}
                      </p>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                      v{a.current_version}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.enabled
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {a.enabled ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right text-sm whitespace-nowrap">
                      <Link
                        to={`/admin/agreements/${encodeURIComponent(a.slug)}`}
                        className="text-sky-600 dark:text-sky-400 hover:underline mr-3"
                      >
                        View / edit
                      </Link>
                      {a.enabled ? (
                        <button
                          type="button"
                          className="text-rose-600 dark:text-rose-400 hover:underline"
                          disabled={updateMeta.isPending}
                          onClick={() =>
                            updateMeta.mutate({
                              slug: a.slug,
                              input: { enabled: false },
                            })
                          }
                        >
                          Archive
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-emerald-600 dark:text-emerald-400 hover:underline"
                          disabled={updateMeta.isPending}
                          onClick={() =>
                            updateMeta.mutate({
                              slug: a.slug,
                              input: { enabled: true },
                            })
                          }
                        >
                          Unarchive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AgreementFormModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        mode="create"
      />
    </AdminLayout>
  );
}
